import { useState, useEffect } from 'react';
import { getSimilarDrivers } from '../../services/api';
import WeightSliders from './WeightSliders';
import { SERIES_COLORS } from './DriverRadar';
import styles from './SimilarityLabPage.module.css';

const fmtLap = s => {
  if (s == null) return '—';
  if (s < 60) return s.toFixed(3);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`;
};

/**
 * Drivers ranked by weighted per-corner-centroid distance to the viewed
 * driver. Checkboxes (max 3) push overlays onto the Radar tab.
 */
export default function SimilarDrivers({
  trackId, viewUserId, vehicleId, lapBasis, weights, onWeights,
  overlayKeys, onOverlays, onShowRadar,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced fetch — slider drags shouldn't fire a query per tick.
  useEffect(() => {
    if (!trackId || vehicleId == null) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      getSimilarDrivers(trackId, { userId: viewUserId, vehicleId, weights, lapBasis, limit: 25 })
        .then(d => { if (!cancelled) { setData(d); setError(null); setLoading(false); } })
        .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [trackId, viewUserId, vehicleId, weights, lapBasis]);

  const toggleOverlay = (key) => {
    if (overlayKeys.includes(key)) {
      onOverlays(overlayKeys.filter(k => k !== key));
    } else if (overlayKeys.length < 3) {
      onOverlays([...overlayKeys, key]);
    }
  };

  if (error) return <div className={styles.error}>{error}</div>;

  const rows = data?.drivers || [];
  const maxDist = rows.length ? Math.max(...rows.map(r => r.style_dist)) : 1;
  const minDist = rows.length ? Math.min(...rows.map(r => r.style_dist)) : 0;
  const closeness = d =>
    maxDist > minDist ? (maxDist - d) / (maxDist - minDist) : 1;

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <span>Most similar drivers{loading ? ' …' : ''}</span>
        <span className={styles.hint}>
          weights steer what “similar” means — line vs pace vs technique
        </span>
      </div>
      <WeightSliders value={weights} onChange={onWeights} />

      {data && (
        <div className={styles.hint} style={{ marginBottom: 8 }}>
          {data.vehicle?.name ? `${data.vehicle.name} — ` : ''}best lap here:{' '}
          <strong>{fmtLap(data.my_best_lap)}</strong>
          {overlayKeys.length > 0 && (
            <button className={styles.linkBtn} onClick={onShowRadar}>
              → show {overlayKeys.length} overlay{overlayKeys.length > 1 ? 's' : ''} on radar
            </button>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className={styles.empty}>
          {data ? 'No comparable drivers at this track (min-passes threshold).' : 'Pick a track…'}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th title="Check to overlay this driver's polygon on the Radar tab (max 3).">Radar</th>
              <th title={'One row per driver + car — the same driver in a '
                       + 'different car is a separate row, because car changes '
                       + 'change technique.'}>Driver</th>
              <th title={'The car this row\'s style was measured in. Blank = laps '
                       + 'with no car attribution.'}>Car</th>
              <th title={'How it\'s computed: for every corner both drivers have '
                       + '≥2 clean passes in, each side\'s passes are averaged into '
                       + 'a per-corner "typical pass" (one average per vector block: '
                       + 'speed, lat G, lon G, line, technique). The distance between '
                       + 'the two averages is taken per block, multiplied by the '
                       + 'slider weights, summed, then averaged over all shared '
                       + 'corners. Smaller = more similar driving. Comparable only '
                       + 'within one result set — it rescales when weights change.'}>
                Style dist
              </th>
              <th title={'Style dist rescaled 0–100% within this result set: the '
                       + 'closest driver gets a full bar, the farthest an empty one. '
                       + 'Relative, not an absolute score.'}>Similarity</th>
              <th title={'Corners where both of you have ≥2 clean passes — the '
                       + 'basis of the comparison. A low count means the distance '
                       + 'rests on little shared ground.'}>Corners</th>
              <th title="This driver's clean corner passes at this track, in this car.">Passes</th>
              <th title="This driver's best leaderboard lap at this track, in this car.">Best lap</th>
              <th title="Their best minus yours — negative means they're faster">Δ vs you</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ovIdx = overlayKeys.indexOf(r.entity_key);
              const delta = (r.best_lap != null && data.my_best_lap != null)
                ? r.best_lap - data.my_best_lap : null;
              const isSelf = r.user_id === (viewUserId || data.user_id);
              return (
                <tr key={r.entity_key}>
                  <td>
                    <input
                      type="checkbox"
                      checked={ovIdx >= 0}
                      disabled={ovIdx < 0 && overlayKeys.length >= 3}
                      onChange={() => toggleOverlay(r.entity_key)}
                    />
                    {ovIdx >= 0 && (
                      <i className={styles.ovSwatch}
                         style={{ background: SERIES_COLORS[ovIdx + 1] }} />
                    )}
                  </td>
                  <td>{r.display_name}{isSelf ? <span className={styles.muted}> (you)</span> : ''}</td>
                  <td>{r.vehicle_name || '—'}</td>
                  <td>{r.style_dist.toFixed(3)}</td>
                  <td>
                    <span className={styles.bar}>
                      <i style={{ width: `${Math.round(closeness(r.style_dist) * 100)}%` }} />
                    </span>
                  </td>
                  <td>{r.shared_corners}</td>
                  <td>{r.passes.toLocaleString()}</td>
                  <td>{fmtLap(r.best_lap)}</td>
                  <td className={delta != null && delta < 0 ? styles.faster : undefined}>
                    {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(2)}s`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
